import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { MousePointerClick, Radio, Trophy, Users } from 'lucide-react';
import { toast } from 'sonner';
import { AnalyticsAgentRow, AnalyticsComparisonWindow, AnalyticsOverview, compareMetricValues } from '@/services/analyticsMetrics';
import {
  FirstPartyAnalyticsRange,
  getAgentAnalytics,
  getFirstPartyAnalyticsOverviewComparison,
} from '@/services/analyticsService';
import {
  AnalyticsEmptyState,
  AnalyticsErrorState,
  AnalyticsLoadingState,
  AnalyticsPageHeader,
  AnalyticsRefreshControl,
  ChartCard,
  MetricCard,
} from '@/components/analytics/AnalyticsShared';
import { useAnalyticsAutoRefresh } from '@/hooks/useAnalyticsAutoRefresh';
import { ANALYTICS_COLORS, formatDeltaText, formatNumber, formatPercent, formatUtcRange } from '@/components/analytics/analyticsDisplay';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useLanguage } from '@/i18n';

export default function AgentAnalytics() {
  const [range, setRange] = useState<FirstPartyAnalyticsRange>('7d');
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [previousOverview, setPreviousOverview] = useState<AnalyticsOverview | null>(null);
  const [comparisonWindow, setComparisonWindow] = useState<AnalyticsComparisonWindow | null>(null);
  const [agents, setAgents] = useState<AnalyticsAgentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useLanguage();

  const loadAnalytics = useCallback(async (silent = false) => {
    try {
      if (silent) setRefreshing(true);
      else setLoading(true);
      setError(null);

      const [overviewResult, agentResult] = await Promise.all([
        getFirstPartyAnalyticsOverviewComparison(range),
        getAgentAnalytics(range),
      ]);

      setOverview(overviewResult.current);
      setPreviousOverview(overviewResult.previous);
      setComparisonWindow(overviewResult.window);
      setAgents(agentResult);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('analyticsLoadFailed');
      setError(message);
      if (!silent) toast.error(message);
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  }, [range, t]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const silentRefresh = useCallback(() => loadAnalytics(true), [loadAnalytics]);
  useAnalyticsAutoRefresh(silentRefresh);

  const topClickedAgent = agents[0];
  const topSelectedAgent = useMemo(
    () => [...agents].sort((a, b) => b.selections - a.selections || a.agent.localeCompare(b.agent))[0],
    [agents],
  );
  const comparisonLabel = t('analyticsComparedWithPrior');

  return (
    <div className="space-y-6 animate-fade-in">
      <AnalyticsPageHeader
        title={t('analyticsAgentsTitle')}
        description={t('analyticsAgentsDescription')}
        range={range}
        onRangeChange={setRange}
        rangeLabel={t('analyticsDateRange')}
      >
        {overview?.start && (
          <p className="mt-2 text-xs text-muted-foreground">
            {t('analyticsUtcRange', { range: formatUtcRange(overview.start, overview.end) })}
          </p>
        )}
        {comparisonWindow?.previous && (
          <p className="mt-1 text-xs text-muted-foreground">
            {t('analyticsPriorUtcRange', { range: formatUtcRange(comparisonWindow.previous.start, comparisonWindow.previous.end) })}
          </p>
        )}
        <AnalyticsRefreshControl
          refreshLabel={t('analyticsRefresh')}
          hintLabel={t('analyticsAutoRefreshHint')}
          refreshing={refreshing}
          disabled={loading}
          onRefresh={silentRefresh}
        />
      </AnalyticsPageHeader>

      {loading ? (
        <AnalyticsLoadingState label={t('analyticsLoading')} />
      ) : error ? (
        <AnalyticsErrorState
          title={t('analyticsErrorTitle')}
          message={error}
          retryLabel={t('analyticsRetry')}
          onRetry={() => loadAnalytics()}
        />
      ) : agents.length === 0 ? (
        <AnalyticsEmptyState title={t('analyticsNoEventsTitle')} description={t('analyticsNoEventsDescription')} />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              title={t('analyticsAffiliateClicks')}
              value={formatNumber(overview?.affiliateClicks || 0)}
              description={t('analyticsAffiliateClicksDescription')}
              icon={MousePointerClick}
              comparisonLabel={comparisonLabel}
              comparisonValue={formatDeltaText(compareMetricValues(overview?.affiliateClicks, previousOverview?.affiliateClicks), {
                newLabel: t('analyticsComparisonNew'),
                noBaselineLabel: t('analyticsComparisonNoBaseline'),
              })}
            />
            <MetricCard
              title={t('analyticsAgentSelections')}
              value={formatNumber(overview?.agentSelections || 0)}
              description={t('analyticsAgentSelectionsDescription')}
              icon={Users}
              comparisonLabel={comparisonLabel}
              comparisonValue={formatDeltaText(compareMetricValues(overview?.agentSelections, previousOverview?.agentSelections), {
                newLabel: t('analyticsComparisonNew'),
                noBaselineLabel: t('analyticsComparisonNoBaseline'),
              })}
            />
            <MetricCard
              title={t('analyticsTopClickedAgent')}
              value={topClickedAgent?.agent || '--'}
              description={t('analyticsTopClickedAgentDescription')}
              icon={Trophy}
            />
            <MetricCard
              title={t('analyticsTopSelectedAgent')}
              value={topSelectedAgent?.agent || '--'}
              description={t('analyticsTopSelectedAgentDescription')}
              icon={Radio}
            />
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <ChartCard title={t('analyticsClicksByAgent')} description={t('analyticsClicksByAgentDescription')}>
              <div className="h-[320px]" role="img" aria-label={t('analyticsClicksByAgent')}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={agents.slice(0, 12)} margin={{ top: 10, right: 20, bottom: 40, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="agent" angle={-30} textAnchor="end" height={70} tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(value: number) => [formatNumber(value), t('analyticsAffiliateClicks')]} />
                    <Bar dataKey="affiliateClicks" name={t('analyticsAffiliateClicks')} fill={ANALYTICS_COLORS[0]} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            <ChartCard title={t('analyticsSelectionShare')} description={t('analyticsSelectionShareDescription')}>
              <div className="h-[320px]" role="img" aria-label={t('analyticsSelectionShare')}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={agents.filter((agent) => agent.selections > 0).slice(0, 8)}
                      dataKey="selections"
                      nameKey="agent"
                      innerRadius={70}
                      outerRadius={110}
                      paddingAngle={2}
                    >
                      {agents.slice(0, 8).map((entry, index) => (
                        <Cell key={entry.agent} fill={ANALYTICS_COLORS[index % ANALYTICS_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => [formatNumber(value), t('analyticsAgentSelections')]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>
          </div>

          <ChartCard title={t('analyticsAgentRanking')} description={t('analyticsAgentRankingDescription')}>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('analyticsAgent')}</TableHead>
                    <TableHead className="text-right">{t('analyticsSelectedShare')}</TableHead>
                    <TableHead className="text-right">{t('analyticsSelections')}</TableHead>
                    <TableHead className="text-right">{t('analyticsClickedShare')}</TableHead>
                    <TableHead className="text-right">{t('analyticsClicks')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agents.map((agent) => (
                    <TableRow key={agent.agent}>
                      <TableCell className="font-medium">{agent.agent}</TableCell>
                      <TableCell className="text-right">{formatPercent(agent.selectionShare)}</TableCell>
                      <TableCell className="text-right">{formatNumber(agent.selections)}</TableCell>
                      <TableCell className="text-right">{formatPercent(agent.clickShare)}</TableCell>
                      <TableCell className="text-right">{formatNumber(agent.affiliateClicks)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </ChartCard>
        </>
      )}
    </div>
  );
}
